import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Users, Truck, BarChart3, LogOut, Menu, ChevronRight, ArrowLeft } from 'lucide-react';
import { Toaster } from '../ui/simple-toaster';
import { api } from '../../utils/api';
import ClientsPanel from './ClientsPanel';
import AllUsersPanel from './AllUsersPanel';
import ClientDetailPanel from './ClientDetailPanel';

const SystemAdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const data = await api('/sysadmin/overview');
      setOverview(data);
    } catch (err) {
      console.error('Failed to load overview:', err);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    const handleResize = () => setSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [loadOverview]);

  const handleViewClient = useCallback((client) => {
    setSelectedClient(client);
    setActiveTab('client-detail');
  }, []);

  const handleBackFromClient = useCallback(() => {
    setSelectedClient(null);
    setActiveTab('clients');
  }, []);

  const menuItems = [
    { id: 'overview', label: 'Platform Overview', icon: BarChart3 },
    { id: 'clients', label: 'All Clients', icon: Building2 },
    { id: 'users', label: 'All Users', icon: Users },
  ];

  const statCards = overview ? [
    { label: 'Clients', value: overview.total_clients, color: 'bg-blue-500/20 text-blue-400', iconBg: 'bg-blue-500/20', icon: Building2 },
    { label: 'Users', value: overview.total_users, color: 'bg-violet-500/20 text-violet-400', iconBg: 'bg-violet-500/20', icon: Users },
    { label: 'Drivers', value: overview.total_drivers, color: 'bg-emerald-500/20 text-emerald-400', iconBg: 'bg-emerald-500/20', icon: Truck },
    { label: 'Vehicles', value: overview.total_vehicles, color: 'bg-amber-500/20 text-amber-400', iconBg: 'bg-amber-500/20', icon: Truck },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="sysadmin-dashboard">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 ${sidebarOpen ? 'w-64' : 'w-0 lg:w-20'} bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col overflow-hidden`}>
        <div className="p-3 sm:p-4 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          {sidebarOpen && (
            <div>
              <span className="font-bold text-white text-base sm:text-lg">LAWD</span>
              <p className="text-xs text-red-400">System Admin</p>
            </div>
          )}
        </div>

        <nav className="flex-1 p-3 sm:p-4 space-y-1 sm:space-y-2">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setSelectedClient(null);
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition text-sm sm:text-base ${
                activeTab === item.id || (item.id === 'clients' && activeTab === 'client-detail')
                  ? 'bg-red-500/20 text-red-400'
                  : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
              }`}
              data-testid={`sysadmin-menu-${item.id}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-3 sm:p-4 border-t border-slate-700">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-slate-400 hover:text-red-400 rounded-xl hover:bg-slate-700/50 transition text-sm sm:text-base"
            data-testid="sysadmin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto min-w-0">
        <header className="bg-slate-800/50 border-b border-slate-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700" data-testid="sysadmin-sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base sm:text-xl font-semibold text-white capitalize">
                {activeTab === 'client-detail' && selectedClient
                  ? selectedClient.name
                  : activeTab.replace('-', ' ')}
              </h1>
              <p className="text-xs sm:text-sm text-red-400">WP Admin Console</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white font-medium text-sm">{user.first_name} {user.last_name}</p>
              <p className="text-xs text-red-400">SYSTEM_ADMIN</p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-500/20 border border-red-500/50 rounded-full flex items-center justify-center">
              <span className="text-red-400 font-medium text-xs sm:text-base">{user.first_name?.[0]}{user.last_name?.[0]}</span>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div data-testid="sysadmin-overview-tab">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {statCards.map((card) => (
                  <div key={card.label} className={`${card.color} rounded-2xl p-5 border border-slate-700/50`} data-testid={`sysadmin-stat-${card.label.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm opacity-80">{card.label}</span>
                      <div className={`w-10 h-10 ${card.iconBg} rounded-xl flex items-center justify-center`}>
                        <card.icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold">{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Quick actions */}
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
                <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setActiveTab('clients')}
                    className="flex items-center gap-3 p-4 bg-slate-700/30 hover:bg-slate-700/60 rounded-xl transition text-left group"
                    data-testid="sysadmin-quick-clients"
                  >
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">View All Clients</div>
                      <div className="text-xs text-slate-400">Manage businesses and subscriptions</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition" />
                  </button>
                  <button
                    onClick={() => setActiveTab('users')}
                    className="flex items-center gap-3 p-4 bg-slate-700/30 hover:bg-slate-700/60 rounded-xl transition text-left group"
                    data-testid="sysadmin-quick-users"
                  >
                    <Users className="w-5 h-5 text-violet-400" />
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">View All Users</div>
                      <div className="text-xs text-slate-400">Filter by role across all clients</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Clients Tab */}
          {activeTab === 'clients' && (
            <ClientsPanel onViewClient={handleViewClient} />
          )}

          {/* All Users Tab */}
          {activeTab === 'users' && (
            <AllUsersPanel onViewClient={handleViewClient} />
          )}

          {/* Client Detail (Impersonate) */}
          {activeTab === 'client-detail' && selectedClient && (
            <ClientDetailPanel
              clientId={selectedClient.client_id}
              clientName={selectedClient.name}
              onBack={handleBackFromClient}
            />
          )}
        </div>
      </main>
      <Toaster />
    </div>
  );
};

export default SystemAdminDashboard;
